import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../../config/api';
import API_CONFIG from '../../config/api';

const normalizeStatusValue = (status) => {
  const normalized = (status || '').toLowerCase();

  switch (normalized) {
    case 'active':
      return 'Active';
    case 'inactive':
      return 'Inactive';
    case 'suspended':
      return 'Suspended';
    default:
      return status || 'Unknown';
  }
};

const prepareUser = (user) => ({
  ...user,
  status: normalizeStatusValue(user.status),
  lastLogin: user.lastLogin ?? 'Never',
  client: user.client ?? ''
});

export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.USERS);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch users';
      return rejectWithValue(message);
    }
  }
);

export const createUser = createAsyncThunk(
  'userManagement/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        API_CONFIG.ENDPOINTS.USERS_CREATE,
        userData
      );
      return response.data || userData;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to create user';
      return rejectWithValue(message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'userManagement/updateUser',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        API_CONFIG.ENDPOINTS.USERS_UPDATE(id),
        data
      );
      return response.data || data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to update user';
      return rejectWithValue(message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'userManagement/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(API_CONFIG.ENDPOINTS.USERS_DELETE(id));
      return id;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Failed to delete user';
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  users: [],
  status: 'idle',
  error: null,
  createStatus: 'idle',
  createError: null,
  updateStatus: 'idle',
  updateError: null,
  updatingUserId: null,
  deleteStatus: 'idle',
  deleteError: null,
  deletingUserId: null
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    resetCreateState(state) {
      state.createStatus = 'idle';
      state.createError = null;
    },
    resetMutationState(state) {
      state.updateStatus = 'idle';
      state.updateError = null;
      state.updatingUserId = null;
      state.deleteStatus = 'idle';
      state.deleteError = null;
      state.deletingUserId = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = (action.payload || []).map(prepareUser);
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch users';
      })
      .addCase(createUser.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createStatus = 'succeeded';
        if (action.payload) {
          state.users = [prepareUser(action.payload), ...state.users];
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createStatus = 'failed';
        state.createError = action.payload || 'Failed to create user';
      })
      .addCase(updateUser.pending, (state, action) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.updatingUserId = action.meta.arg.id;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.updatingUserId = null;

        const updatedUserPayload = action.payload || {};
        const userId = updatedUserPayload.id || action.meta.arg.id;

        state.users = state.users.map((user) => {
          if (user.id !== userId) {
            return user;
          }

          const mergedUser = {
            ...user,
            ...updatedUserPayload
          };

          return prepareUser(mergedUser);
        });
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.updateError = action.payload || 'Failed to update user';
        state.updatingUserId = null;
      })
      .addCase(deleteUser.pending, (state, action) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
        state.deletingUserId = action.meta.arg;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.deletingUserId = null;
        const deletedId = action.payload || action.meta.arg;
        state.users = state.users.filter((user) => user.id !== deletedId);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload || 'Failed to delete user';
        state.deletingUserId = null;
      })
      ;
  }
});

export const { resetCreateState, resetMutationState } = userManagementSlice.actions;

export default userManagementSlice.reducer;

